import { m } from "@plannello/i18n/messages";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Form } from "react-aria-components";
import { AuthCard } from "@/components/shell/AuthCard";
import { LocaleSwitch } from "@/components/shell/LocaleSwitch";
import { Button, buttonStyles, InlineError, PasswordField, Skeleton } from "@/components/ui";
import { ApiError, api } from "@/lib/api";
import { focusFirstInvalid } from "@/lib/forms";
import { applyLocale } from "@/lib/locale";
import type { Locale } from "@/lib/queries";
import { describeError } from "@/lib/session";

export const Route = createFileRoute("/reset-password/$token")({ component: ResetPage });

type ResetInfo = { email: string; name: string; locale: Locale };

function problemOf(e: unknown): { title: string; body: string } | null {
  if (!(e instanceof ApiError)) return null;
  switch (e.code) {
    case "reset_used":
      return { title: m.reset_used_title(), body: m.reset_used_body() };
    case "reset_expired":
      return { title: m.reset_expired_title(), body: m.reset_expired_body() };
    case "account_disabled":
      return { title: m.reset_disabled_title(), body: m.reset_disabled_body() };
    case "reset_invalid":
      return { title: m.reset_invalid_title(), body: m.reset_invalid_body() };
    default:
      return null;
  }
}

const toLogin = { redirect: undefined, error: undefined } as const;

function ResetPage() {
  const { token } = Route.useParams();
  const q = useQuery({
    queryKey: ["password-reset", token],
    queryFn: () => api.get<ResetInfo>(`/api/v1/auth/password-resets/${token}`),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [errors, setErrors] = useState<{ password?: string; confirm?: string; form?: string }>({});
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const [closed, setClosed] = useState<{ title: string; body: string } | null>(null);

  const locale = q.data?.locale;
  useEffect(() => {
    if (locale) applyLocale(locale);
  }, [locale]);

  const signInLink = (
    <Link
      to="/login"
      search={toLogin}
      className={buttonStyles({ variant: "primary", size: "lg", className: "w-full" })}
    >
      {m.auth_sign_in()}
    </Link>
  );

  if (q.isPending) {
    return (
      <AuthCard title={m.reset_title()}>
        <div role="status" aria-busy="true" className="flex flex-col gap-4">
          <span className="sr-only">{m.common_loading()}</span>
          {[0, 1].map((i) => (
            <Skeleton key={i} className="h-8 w-full" />
          ))}
        </div>
      </AuthCard>
    );
  }
  const problem = closed ?? (q.isError ? problemOf(q.error) : null);
  if (problem) {
    return (
      <AuthCard title={problem.title} intro={problem.body} footer={<LocaleSwitch />}>
        {signInLink}
      </AuthCard>
    );
  }
  if (q.isError) {
    return (
      <AuthCard title={m.reset_title()}>
        <InlineError
          message={describeError(q.error, { network: m.error_network(), generic: m.error_generic() })}
          onRetry={() => void q.refetch()}
          isRetrying={q.isFetching}
        />
      </AuthCard>
    );
  }
  if (done) {
    return (
      <AuthCard title={m.reset_done_title()} intro={m.reset_done_body()} footer={<LocaleSwitch />}>
        {signInLink}
      </AuthCard>
    );
  }

  async function submit() {
    const next: typeof errors = {};
    if (password.length < 10) next.password = m.sec_err_too_short();
    if (confirm !== password) next.confirm = m.sec_err_mismatch();
    setErrors(next);
    if (next.password || next.confirm) {
      focusFirstInvalid();
      return;
    }
    setPending(true);
    try {
      await api.post(`/api/v1/auth/password-resets/${token}`, { password });
      setDone(true);
    } catch (e) {
      const p = problemOf(e);
      if (p) setClosed(p);
      else if (e instanceof ApiError && e.fields?.password) setErrors({ password: m.sec_err_too_short() });
      else setErrors({ form: describeError(e, { network: m.error_network(), generic: m.error_generic() }) });
    } finally {
      setPending(false);
    }
  }

  return (
    <AuthCard title={m.reset_title()} intro={m.reset_intro({ email: q.data.email })} footer={<LocaleSwitch />}>
      <Form
        validationBehavior="aria"
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        className="flex flex-col gap-4"
      >
        {errors.form ? <InlineError message={errors.form} /> : null}
        <PasswordField
          label={m.sec_password_new()}
          description={m.sec_password_rule()}
          name="password"
          autoComplete="new-password"
          autoFocus
          value={password}
          onChange={(v) => {
            setPassword(v);
            setErrors({ ...errors, password: undefined });
          }}
          error={errors.password}
        />
        <PasswordField
          label={m.sec_password_confirm()}
          name="confirm"
          autoComplete="new-password"
          value={confirm}
          onChange={(v) => {
            setConfirm(v);
            setErrors({ ...errors, confirm: undefined });
          }}
          error={errors.confirm}
        />
        <p className="text-xs text-pretty text-fg-muted">{m.reset_signout_note()}</p>
        <Button type="submit" variant="primary" size="lg" isPending={pending} className="mt-1 w-full">
          {m.reset_submit()}
        </Button>
      </Form>
    </AuthCard>
  );
}
